import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Tab,
  Tabs,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BaseWidgetProps, WidgetWrapper } from "shared-ui";
import { useClaims } from "../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../auth/authorizationTypes";
import useSearchOrders from "@features/orders-features/hooks/useSearchOrders";
import { useOrdersStream } from "@features/orders-features/hooks/useOrdersStream";
import { OrderSyncIndicator } from "@features/orders-features/components/OrderSyncIndicator";
import {
  DEFAULT_FILTER,
  DEFAULT_SORT,
} from "@features/orders-features/constants/viewOrdersConstants";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import OrderList from "./Components/OrderList";
import { OrderViewType } from "./types/OrderViewType";
import LoadingSkeleton from "@widgets/ecommerce/Components/LoadingSkeleton";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";


const ORDER_VIEW_TABS: OrderViewType[] = [
  OrderViewType.All,
  OrderViewType.My,
  OrderViewType.Storefront,
];
const RECENT_ORDERS_LIMIT = 5;

const baseUrl = getShiComBaseUrl();
const contactAccountTeamUrl = `${baseUrl}/CustomerService/contact-us`;

export default function RecentOrdersWidget({
  pageLayoutWidget,
}: Readonly<BaseWidgetProps>) {
  const navigate = useNavigate();
  const { hasPermission, getClaimValue } = useClaims();

  const searchMyOrders = hasPermission(ShiOnePermission.orderSearchMy);
  const searchOrgUnitOrders = hasPermission(
    ShiOnePermission.orderSearchOrgUnit,
  );
  const isAdmin = searchOrgUnitOrders;

  const [activeTab, setActiveTab] = useState<OrderViewType>(
    isAdmin ? OrderViewType.All : OrderViewType.My
  );

  useEffect(() => {
    if (!isAdmin && activeTab !== OrderViewType.My) {
      setActiveTab(OrderViewType.My);
    }
  }, [isAdmin, activeTab]);

  const currentCrmAccountId = getClaimValue("crmAccountId") ?? undefined;
  const rawOrgUnitIdCurrent = getClaimValue("orgUnitIdCurrent");
  const orgUnitIdCurrent =
    rawOrgUnitIdCurrent != null ? Number(rawOrgUnitIdCurrent) : null;
  const currentEmployeeId = Number(getClaimValue("shiComEmployeeId"));
  const filter =
    activeTab === OrderViewType.Storefront && Number.isFinite(orgUnitIdCurrent)
      ? {
          ...DEFAULT_FILTER,
          include: {
            ...DEFAULT_FILTER.include,
            orgUnitIds: [String(orgUnitIdCurrent)],
          },
        }
      : DEFAULT_FILTER;

  const { syncStatus, lastSyncedAt } = useOrdersStream();

  // Source the widget from the same unified order search the View Orders page uses
  // (top 5, most recent first) so status renders identically across surfaces. Sharing
  // the OrdersQueryKeys.Search cache also lets the sync stream auto-refresh the widget.
  const { data, isLoading, isError, isFetching } = useSearchOrders({
    view: activeTab,
    crmAccountId:
      activeTab === OrderViewType.Storefront ? currentCrmAccountId : undefined,
    employeeId: currentEmployeeId,
    filter,
    sortBy: DEFAULT_SORT.field,
    sortDirection: DEFAULT_SORT.sort ?? "desc",
    page: 1,
    pageSize: RECENT_ORDERS_LIMIT,
  });

  const filteredOrders = data?.items ?? [];

  if (!searchMyOrders && !searchOrgUnitOrders) return null;

  return (
    <WidgetWrapper
      pageLayoutWidget={{ ...pageLayoutWidget }}
      cardHeaderDivider
      headerAction={
        syncStatus !== "idle" || isFetching ? (
          <OrderSyncIndicator
            variant="compact"
            syncStatus={syncStatus}
            lastSyncedAt={lastSyncedAt}
            isRefreshing={isFetching}
          />
        ) : undefined
      }
      noPadding
      error={isError}
      cardActions={
        <Button
          aria-label="View all orders"
          onClick={(e) => {
            e.stopPropagation();
            navigate(AppendIfPrefixExists("/orders/management"));
          }}
          {...telemetryProps({
            name: "orders_recent_view_all",
            section: "orders_recent_widget",
            label: "View all orders",
          })}
        >
          VIEW ALL ORDERS
        </Button>
      }
      errorContactAccountTeamLink={contactAccountTeamUrl}
      hideWidgetContentOnError={true}
    >
      {isAdmin && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue: OrderViewType) => setActiveTab(newValue)}
          aria-label="recent orders tabs"
        >
          {ORDER_VIEW_TABS.map((tab) => (
            <Tab
              key={tab}
              value={tab}
              label={`${tab.toUpperCase()} ORDERS`}
              {...telemetryProps({
                name: "orders_recent_tab",
                section: "orders_recent_widget",
                label: `${tab.toUpperCase()} ORDERS`,
                id: tab,
              })}
            />
          ))}
        </Tabs>
      )}
      <Box sx={{ p: 0 }}>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <OrderList orders={filteredOrders} type={activeTab} />
        )}
      </Box>
    </WidgetWrapper>
  );
}
